import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  NgZone,
  OnDestroy,
  ViewChild,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { html } from '@codemirror/lang-html';
import { json } from '@codemirror/lang-json';
import { EditorState } from '@codemirror/state';
import { EditorView, type ViewUpdate } from '@codemirror/view';
import { tryPrettifyJsonString } from '@app/formly/validators/json-string-validator';
import { basicSetup } from 'codemirror';
import { BasefieldV2Component } from '../basefield-v2/basefield-v2.component';

@Component({
  selector: 'codemirror-v2',
  templateUrl: './codemirror-v2.component.html',
  styleUrls: ['./codemirror-v2.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CodemirrorV2Component
  extends BasefieldV2Component
  implements AfterViewInit, OnDestroy
{
  @ViewChild('host', { static: true }) hostRef!: ElementRef<HTMLDivElement>;

  ngZone = inject(NgZone);
  destroyRef = inject(DestroyRef);

  view: EditorView | undefined;

  ngAfterViewInit() {
    const parent = this.hostRef.nativeElement;
    const language = this.props?.codemirrorLanguage === 'html' ? html() : json();
    const rowCount = typeof this.props?.rows === 'number' ? this.props.rows : 12;
    const blockHeight = `${Math.max(160, rowCount * 20)}px`;
    parent.style.setProperty('--codemirror-v2-block-height', blockHeight);

    const initialDoc = String(this.formControl.value ?? '');
    const extensions = [
      basicSetup,
      language,
      EditorView.theme({
        '&': {
          backgroundColor: 'transparent',
          height: '100%',
          minHeight: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
        },
        '.cm-scroller': {
          flex: '1 1 auto',
          overflow: 'auto',
          minHeight: '0',
        },
        '.cm-gutters': {
          backgroundColor: 'transparent',
          borderColor: 'transparent',
        },
        '.cm-activeLineGutter': {
          backgroundColor: 'transparent',
        },
        '.cm-activeLine': {
          backgroundColor: 'transparent',
        },
        '&.cm-focused': {
          outline: 'none',
        },
      }),
      EditorView.domEventHandlers({
        focus: (_event: FocusEvent, v: EditorView) => {
          if (!this.props?.prettifyJsonOnFocus) {
            return;
          }
          const doc = v.state.doc.toString();
          const pretty = tryPrettifyJsonString(doc);
          if (pretty !== null && pretty !== doc) {
            v.dispatch({
              changes: { from: 0, to: v.state.doc.length, insert: pretty },
            });
            this.ngZone.run(() => {
              this.formControl.setValue(pretty, { emitEvent: true });
              this.formControl.markAsDirty();
              this.formControl.updateValueAndValidity();
            });
          }
        },
      }),
      EditorView.updateListener.of((update: ViewUpdate) => {
        if (!update.docChanged) {
          return;
        }
        const text = update.state.doc.toString();
        this.ngZone.run(() => {
          this.formControl.setValue(text, { emitEvent: true });
          this.formControl.markAsDirty();
          this.formControl.updateValueAndValidity();
        });
      }),
    ];

    this.view = new EditorView({
      state: EditorState.create({ doc: initialDoc, extensions }),
      parent,
    });

    this.formControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((val) => {
      const next = String(val ?? '');
      if (!this.view || next === this.view.state.doc.toString()) {
        return;
      }
      this.view.dispatch({
        changes: { from: 0, to: this.view.state.doc.length, insert: next },
      });
    });
  }

  ngOnDestroy() {
    this.view?.destroy();
  }
}
